import type { Store } from "@inferno/renderer-shared-core";
import { Elements, RecurlyProvider } from "@recurly/react-recurly";
import { inject } from "mobx-react";
import { PropsWithChildren, useCallback, useEffect, useState } from "react";

import { isPWSSessionBackend, PWSSessionBackend, PWSSessionState } from "@inferno/renderer-shared-core";
import { Remote } from "../../../../../core/components/remote/Remote.component";
import { Button, ButtonKind, FauxButton } from "../../../../../core/ui";
import Heading from "../../../../../core/ui/Heading.component";
import { axiosLoaderWrapper } from "../../../lib/coastCommon";
import { recurlyLoader } from "../../../lib/recurly";
import { CoastAlertDisplayError } from "../common/coast-alert/CoastAlertDisplay.component";
import { UsersButton } from "../common/UsersButton.components";
import { CoastSubscribe } from "./CoastSubscribe.component";

interface CoastSubscribeWrapperProps {
  setIsGift: (v: boolean) => void;
  session: PWSSessionBackend;
  onCancel: () => void;
  store?: Store;
  isGift: boolean;
}

const CoastSubscribeWrapperBase = ({ children }: PropsWithChildren<Record<string, unknown>>) => (
  <div className="component-subscribe-wrapper">{children}</div>
);

export const CoastSubscribeWrapper = inject("store")(
  ({ onCancel, isGift, setIsGift, session, store }: CoastSubscribeWrapperProps) => {
    if (!store || !isPWSSessionBackend(session)) {
      return null;
    }

    // only need to trigger re-render on login
    const [isLoggedIn, setIsLoggedIn] = useState(session.authenticated);

    const handleSessionChange = useCallback(
      (state: PWSSessionState) => {
        if (state.authenticated) {
          if (!isLoggedIn) {
            setIsLoggedIn(true);
          }
        } else if (isLoggedIn) {
          setIsLoggedIn(false);
        }
      },
      [isLoggedIn],
    );

    useEffect(() => {
      if (!isLoggedIn) {
        session.onStatusChanged.subscribe(handleSessionChange);
      }

      return () => session.onStatusChanged.unsubscribe(handleSessionChange);
    }, [handleSessionChange, isLoggedIn, session.onStatusChanged]);

    useEffect(() => {
      if (!isLoggedIn) {
        session.showUsersPopup();
      }
    }, [isLoggedIn, session]);

    if (!session.authenticated) {
      return (
        <CoastSubscribeWrapperBase>
          <main className="component-subscribe">
            <Heading level={2}>Seems like you&apos;re not logged in.</Heading>
            <p className="section-info">Please login to continue.</p>
            <UsersButton click={() => session.showUsersPopup()}>Login</UsersButton>
          </main>
          <hr className="divider" />
          <p className="section-info">
            You must first create an account in order to select a subscription. &nbsp; If you do not have an account
            please go to the &nbsp;
            <FauxButton kind={ButtonKind.CUSTOMIZED} to="/sign-up?step=email">
              Sign Up page
            </FauxButton>
            .
          </p>
        </CoastSubscribeWrapperBase>
      );
    }

    if (!isGift && session.state?.hasActiveSubscription) {
      // TODO: once we can handle future subs, revisit this (PN-1509)
      return (
        <CoastSubscribeWrapperBase>
          <main className="component-subscribe">
            <Heading level={2} className="section-title">
              We&apos;ve found an active subscription for this account.
            </Heading>
            <p className="section-info">
              For more details go to &nbsp;
              <Button
                click={() => session.showUsersPopup("account")}
                className="button-info-inline"
                kind={ButtonKind.CUSTOMIZED}
              >
                My Account
              </Button>
              .
            </p>
            <p className="section-info">
              <Button click={() => setIsGift(true)} className="button-info-inline" kind={ButtonKind.CUSTOMIZED}>
                Give as a gift
              </Button>
            </p>

            <FauxButton className="return-to-site" to="/">
              Return to Site
            </FauxButton>
          </main>
        </CoastSubscribeWrapperBase>
      );
    }

    return (
      <CoastSubscribeWrapperBase>
        <Remote loader={recurlyLoader} cacheKey={"recurly-lib"} showLoading={true}>
          {({ data: loaded }) =>
            loaded ? (
              <Remote
                loader={() => axiosLoaderWrapper(() => session.memberService.listSubscriptionPlans())}
                cacheKey={"coast-all-subscription-plans"}
                showLoading={true}
              >
                {({ data: availablePlans }) =>
                  availablePlans ? (
                    <RecurlyProvider publicKey={store.env.COAST_RECURLY_PUBLIC_KEY}>
                      <Elements>
                        <CoastSubscribe
                          availablePlans={availablePlans}
                          setIsGift={setIsGift}
                          onCancel={onCancel}
                          session={session}
                          isGift={isGift}
                          isAccountCreated={true}
                        />
                      </Elements>
                    </RecurlyProvider>
                  ) : null
                }
              </Remote>
            ) : (
              <CoastAlertDisplayError isDisplayed={true} />
            )
          }
        </Remote>
      </CoastSubscribeWrapperBase>
    );
  },
);
